import { useEffect, useState } from 'react'

import {
  setRouteOptimizationConflicts,
  useCloseModal,
  useConfigureModalController,
  useModalController,
  useRouteOptimizationConflicts,
} from '@/atoms'

export type OptimizationStrategy = 'fleetSavings' | 'balancedPerformance'
export type DriverUsage = 'minimizeDrivers' | 'useAllActiveDrivers'
export type BalanceBy = 'orders' | 'workingTime' | 'distance' | 'loadType'
export type ConstraintScenario = 'none' | 'minHours' | 'minLoad' | 'both'

export interface ModalData {
  strategy: OptimizationStrategy
  driverUsage: DriverUsage
  balanceBy: BalanceBy | ''
  loadType: string
  ignoreConstraints: boolean
  constraintScenario: ConstraintScenario
  affectedVehiclesCount: number
  // True when the affected vehicles include date-specific exceptions rather than
  // just default profile settings — swaps the alert body to the "…including
  // specific date exceptions" copy. Defaults to `false` when the BE omits it.
  hasExceptionConflict: boolean
  submitAttempted: boolean
}

type ModalState = 'ready'

export const modalId = 'routeOptimization'

export const useController = () => useModalController<ModalData, ModalState>(modalId)

// Map the BE conflict summary (single source of truth) to the alert's scenario +
// affected-vehicles count. `type`: HOUR → minimum working hours, LOAD → minimum
// load, ALL → both; `count` is used directly.
function scenarioFromConflicts(
  result: uui.domain.actions.rpc.route.ConflictVehicleSettingsResponse,
): {
  constraintScenario: ConstraintScenario
  affectedVehiclesCount: number
  hasExceptionConflict: boolean
} {
  if (!result.hasConflict) {
    return { constraintScenario: 'none', affectedVehiclesCount: 0, hasExceptionConflict: false }
  }

  const constraintScenario: ConstraintScenario =
    result.type === 'ALL'
      ? 'both'
      : result.type === 'HOUR'
        ? 'minHours'
        : result.type === 'LOAD'
          ? 'minLoad'
          : 'none'

  return {
    constraintScenario,
    affectedVehiclesCount: result.count,
    // Older backends omit this field — treat as no exceptions rather than crash/undefined.
    hasExceptionConflict: result.hasExceptionConflict ?? false,
  }
}

export const useConfigureController = () => {
  const close = useCloseModal()
  const prefetchedConflicts = useRouteOptimizationConflicts()

  // Conflicts are owned by the BE (single source of truth) and prefetched by the
  // Custom Build trigger, so the modal opens with the alert already in place.
  const [prefetchInit] = useState(() =>
    prefetchedConflicts ? scenarioFromConflicts(prefetchedConflicts) : null,
  )

  // The modal always opens at system defaults (Fleet savings + Minimize drivers) —
  // the user's last selection is intentionally not persisted.
  const [options] = useState(() => ({
    createInitialState: () => ({
      close,
      data: {
        strategy: 'fleetSavings' as const,
        driverUsage: 'minimizeDrivers' as const,
        balanceBy: '' as BalanceBy | '',
        loadType: '',
        ignoreConstraints: false,
        constraintScenario: prefetchInit?.constraintScenario ?? ('none' as const),
        affectedVehiclesCount: prefetchInit?.affectedVehiclesCount ?? 0,
        hasExceptionConflict: prefetchInit?.hasExceptionConflict ?? false,
        submitAttempted: false,
      },
    }),
  }))

  const ctrl = useConfigureModalController<ModalData, ModalState>(modalId, options)

  // One-shot atom consumed by the initial state — clear it once read so a later
  // open doesn't reuse stale conflicts.
  useEffect(() => {
    if (prefetchedConflicts) setRouteOptimizationConflicts(null)
  }, [prefetchedConflicts])

  return ctrl
}
